// Connection constraint engine. A wire is legal only when the DeviceModel
// declares a matching rule, and single-input receivers (selectors / patches)
// reject a second wire — except a USB output, which takes the two channels of a
// mono pair as two wires.

import { isSingleInput, parseRef, ref } from "../models/types";
import type { DeviceModel, NodeKind, RoutingRule } from "../models/types";
import type { NodeParams, Plan, PlanConnection } from "./plan";
import { hasConnection } from "./plan";
import { connParamContestKey, nodeParamContestKey } from "./plan-history";
import {
  BUS_TYPE_FIXED,
  BUS_TYPE_VARI,
  INSERT_FX_NONE,
  PAN_BAL_BAL,
  PAN_BAL_PAN,
  STEREO_PAN_DEFAULT,
} from "./control/params";
import { LEVEL_OFF_DB } from "./control/vd";

// Language-agnostic failure codes. The UI maps these to localized messages so
// core stays free of any i18n dependency.
export type ConnectError = "noRule" | "duplicate" | "singleInput" | "monoPairOnly";

export interface ConnectResult {
  ok: boolean;
  reason?: ConnectError;
}

function findRule(model: DeviceModel, from: string, to: string): RoutingRule | undefined {
  return model.rules.find((rule) => rule.from === from && rule.to === to);
}

export function ruleKind(model: DeviceModel, from: string, to: string): RoutingRule["kind"] | undefined {
  return findRule(model, from, to)?.kind;
}

/** Whether this route is a structural wire that the user may not remove. */
export function isFixedConnection(model: DeviceModel, from: string, to: string): boolean {
  return findRule(model, from, to)?.fixed === true;
}

/** Whether the input `to` is a receiver the unit never leaves without a source
 *  (`DeviceModel.requiredSources`). */
export function requiresSource(model: DeviceModel, to: string): boolean {
  return Object.hasOwn(model.requiredSources, to);
}

/** Whether `from` -> `to` is the only wire into a receiver the unit never leaves without a
 *  source: the one wire the board keeps rather than deletes, and replaces when another
 *  source is drawn onto it. */
export function isLastRequiredSource(model: DeviceModel, plan: Plan, from: string, to: string): boolean {
  return requiresSource(model, to) && !plan.connections.some((c) => c.to === to && c.from !== from);
}

/**
 * The receivers a change leaves in a shape the unit cannot hold that `before` did not
 * already have: a single-input receiver carrying more wires than it takes (two sources, or
 * on a USB output anything but one source or the two channels of a mono pair), or a
 * receiver the unit never leaves without a source left with none. A plan that arrived with
 * such a receiver is not blamed on the change.
 */
export function wireShapeBroken(model: DeviceModel, before: Plan, after: Plan): string[] {
  const overfilled = (p: Plan): Set<string> =>
    new Set(
      validatePlan(model, p)
        .filter((x) => x.reason === "singleInput" || x.reason === "monoPairOnly")
        .map((x) => x.to),
    );
  const was = overfilled(before);
  const broken = [...overfilled(after)].filter((to) => !was.has(to));
  for (const to of Object.keys(model.requiredSources)) {
    const had = before.connections.some((c) => c.to === to);
    if (had && !after.connections.some((c) => c.to === to)) broken.push(to);
  }
  return broken;
}

// Whether a send carries a PRE/POST tap: a send's PRE/POST is taken relative to
// the STEREO main-fader level, so only the STEREO main-fader paths (CH / FX
// channel → STEREO, which ARE that reference) carry no tap. Every other send
// (-> MIX / FX) does. This is independent of `fixed`: the FX channel → MIX sends
// are fixed (non-removable routing) yet still expose a PRE/POST tap.
export function sendHasTap(model: DeviceModel, from: string, to: string): boolean {
  return ruleKind(model, from, to) === "send" && parseRef(to).nodeId !== "bus.stereo";
}

// Whether a route carries a per-send ON switch (the SEND_ON of a CH/FX -> MIX/FX
// send, the MIX -> STEREO "TO ST", or the CH/FX -> STEREO assign ON). Every tapped
// send has one; so do the fixed routes into STEREO — the MIX -> STEREO sendSwitch
// and, since firmware V1.3, the CH/FX -> STEREO main paths (a post-fader STEREO
// assign ON, distinct from the channel master). Lets the UI ask the topology rather
// than re-deriving it from tap + kind proxies.
export function sendHasOn(model: DeviceModel, from: string, to: string): boolean {
  if (sendHasTap(model, from, to)) return true;
  return isFixedConnection(model, from, to) && parseRef(to).nodeId === "bus.stereo";
}

// The two MIX-bus "hidden mode" locks that gate a send's controls, resolved from
// the destination bus's node params: FIXED BUS Type leaves a send only its ON — the
// unit takes it after the source's fader at a fixed level, placed by the source's own PAN / BAL,
// so the LEVEL, the PRE/POST tap and the send's own PAN are all inert — and Pan Link
// ties each send pan to its source's own PAN / BAL, under either BUS Type: the plan keeps
// it through FIXED and the write sends it there too. A send pan turned under Pan Link
// moves the source's own (`panLinkedSendPan`).
// Only MIX 1 / MIX 2 carry these; any other destination returns both false.
// Shared by the inspector (which drops the gated controls), the console (which
// renders them read-only) and the MIDI catalogue (which refuses their writes).
export function mixSendLocks(plan: Plan, destId: string): { busFixed: boolean; panLinked: boolean } {
  const np = plan.nodeParams[destId];
  const isMix = destId === "bus.mix1" || destId === "bus.mix2";
  const busFixed = isMix && (np?.busType ?? BUS_TYPE_VARI) === BUS_TYPE_FIXED;
  const panLinked = isMix && np?.panLink === true;
  return { busFixed, panLinked };
}

const MIX_BUSES = ["bus.mix1", "bus.mix2"] as const;

/** A node, and its partner when the two are a STEREO-linked pair — the nodes an edit and the
 *  pair mirror behind it write. */
export function withLinkedPartner(model: DeviceModel, plan: Plan, id: string): Set<string> {
  const partner = isStereoLinkedPair(model, plan, id) ? partnerChannel(model, id) : undefined;
  return new Set(partner ? [id, partner] : [id]);
}

/** Every send pan into a MIX bus set to its source's own pan / balance — the pan on the source's
 *  fixed main path into STEREO — which is where the unit holds it while the MIX's Pan Link is on:
 *  it sets them there when the link turns on and leaves them there when it turns off. Called on
 *  either edge. A send from a source in `read` is left as it is: a device read that took it holds
 *  the unit's own value. Returns the contest keys it wrote; each can land on the value already
 *  there, so the plan's diff cannot name them. */
export function sendPansToSources(plan: Plan, busId: string, read: ReadonlySet<string> = new Set()): string[] {
  return setSendPans(plan, busId, (source) => !read.has(source)).written;
}

/** What the unit does to a MIX bus's send bank when its BUS Type is set to `busType`: every
 *  send level into it drops to -∞, and every send ON goes OFF under FIXED and ON under VARI.
 *  The PRE/POST tap and the send pan stay as they are. Returns the contest keys it wrote;
 *  each can land on the value already there, so the plan's diff cannot name them. */
export function resetMixSendBank(plan: Plan, busId: string, busType: number): string[] {
  const written: string[] = [];
  for (const c of plan.connections) {
    if (c.to !== ref(busId, "in") || c.kind !== "send") continue;
    c.params = { ...c.params, level: LEVEL_OFF_DB, on: busType !== BUS_TYPE_FIXED };
    written.push(connParamContestKey(c.from, c.to, "level"), connParamContestKey(c.from, c.to, "on"));
  }
  return written;
}

/** While a MIX bus's Pan Link is on, the unit carries every send pan into it along with its
 *  source's own pan / balance. The send pans from `sources` into every linked MIX, set to that
 *  value after a source's position moved. Returns the contest keys whose value it changed. */
export function alignLinkedSendPans(plan: Plan, sources: ReadonlySet<string>): string[] {
  const changed: string[] = [];
  for (const bus of MIX_BUSES)
    if (plan.nodeParams[bus]?.panLink === true) changed.push(...setSendPans(plan, bus, (s) => sources.has(s)).changed);
  return changed;
}

/** A send pan turned while its MIX's Pan Link is on moves the source's own pan / balance, as the
 *  unit's SEND TO screen does: the value lands on the source's fixed main path into STEREO, and
 *  `alignLinkedSendPans` then carries it onto every linked send. Returns the contest key it wrote. */
export function panLinkedSendPan(plan: Plan, source: string, pan: number): string[] {
  const main = plan.connections.find((m) => m.from === ref(source, "out") && m.to === ref("bus.stereo", "in"));
  if (!main) return [];
  main.params = { ...main.params, pan };
  return [connParamContestKey(main.from, main.to, "pan")];
}

/** A send source's own pan / balance: the pan on its fixed main path into STEREO, 0 where the
 *  plan carries none. */
export function sourcePan(plan: Plan, source: string): number {
  const main = plan.connections.find((m) => m.from === ref(source, "out") && m.to === ref("bus.stereo", "in"));
  return main?.params?.pan ?? 0;
}

function setSendPans(
  plan: Plan,
  busId: string,
  take: (source: string) => boolean,
): { written: string[]; changed: string[] } {
  const written: string[] = [];
  const changed: string[] = [];
  for (const c of plan.connections) {
    if (c.to !== ref(busId, "in") || c.kind !== "send") continue;
    const source = parseRef(c.from).nodeId;
    if (!take(source)) continue;
    const pan = sourcePan(plan, source);
    const key = connParamContestKey(c.from, c.to, "pan");
    if (c.params?.pan !== pan) changed.push(key);
    c.params = { ...c.params, pan };
    written.push(key);
  }
  return { written, changed };
}

/** Whether a send is taken ahead of the source channel's fader: a tapped send whose tap
 *  is PRE, into anything but a FIXED MIX bus, which takes every send after the fader
 *  whatever its tap holds (mixSendLocks). What the board, the console and the ducker
 *  note all mean by a PRE send. */
export function sendIsPreFader(model: DeviceModel, plan: Plan, conn: PlanConnection): boolean {
  if (conn.params?.tap !== "pre" || !sendHasTap(model, conn.from, conn.to)) return false;
  return !mixSendLocks(plan, parseRef(conn.to).nodeId).busFixed;
}

// Whether a send's PRE/POST tap can be written to the device from software — the
// single source of truth for that capability (translate suppresses the write,
// inspector turns the tap read-only while live; see callers). CH -> FX taps are
// not writable: the broker reports max_value=0 for them (193/197/320/324, every
// instance), so a software write of PRE (1) is rejected with response_code 400 —
// only the device's own LCD can set them. Reading them is fine (0/1 both come
// back), so readback still reflects the true device tap. CH -> MIX and
// FX-channel -> MIX taps (max_value=1) are writable. This concerns the *device*
// only: the plan's tap field stays freely editable in the planner regardless.
// Confirmed by a live broker probe (2026-06-22).
export function sendTapWritable(model: DeviceModel, from: string, to: string): boolean {
  return sendHasTap(model, from, to) && !parseRef(to).nodeId.startsWith("bus.fx");
}

// Classify a channel's "direct out" tap by destination, or null when `from → to`
// is not one. On the device this is the channel's CH OUT, taken at its Rec Point —
// which the block diagram places BEFORE the fader and the Ducker. So the fader, pan
// and Ducker never reach this route; only a bus source (STEREO / MIX) carries the
// post-Ducker signal to the same outputs. The routing kind already carries the
// destination split — a channel → USB MAIN / SUB is `patch`, a channel → microSD
// Rec is `record` — so callers read the concept here rather than re-deriving it
// from node-id spelling. (A bus → USB is `patch` too, hence the source-kind test;
// the DAW Rec 1:1 taps share this trait but are fixed, so they are not editable.)
export function directOutTarget(model: DeviceModel, from: string, to: string): "usb" | "sdRec" | null {
  const kind = ruleKind(model, from, to);
  if (kind !== "patch" && kind !== "record") return null;
  if (model.nodes.find((n) => n.id === parseRef(from).nodeId)?.kind !== "channel") return null;
  return kind === "patch" ? "usb" : "sdRec";
}

// A ducker's key (sidechain trigger) select, classified by source. A channel key is
// its CH OUT — the same Rec Point tap as a direct out (directOutTarget), upstream of
// that channel's fader and Ducker, so the source channel's fader / mute never change
// the trigger. A bus key (STEREO / MIX) is post-fader instead. Returns the source
// kind, or null when `from → to` is not a ducker key.
export function duckerKeySource(model: DeviceModel, from: string, to: string): "channel" | "bus" | null {
  if (ruleKind(model, from, to) !== "key") return null;
  return model.nodes.find((n) => n.id === parseRef(from).nodeId)?.kind === "channel" ? "channel" : "bus";
}

/** Whether a wire leaves a channel ahead of its mute (ON) and fader: a direct out or a channel
 *  key, both the channel's CH OUT at its Rec Point. The channel's own mute and Ducker never
 *  reach it. */
export function channelTapAheadOfMute(model: DeviceModel, from: string, to: string): boolean {
  return directOutTarget(model, from, to) !== null || duckerKeySource(model, from, to) === "channel";
}

/** Whether a wire takes its source channel ahead of the channel's Ducker: a tap ahead of the
 *  mute, or a PRE send (sendIsPreFader). The STEREO main path and every POST send — a send
 *  into a FIXED MIX bus among them — carry the ducked signal. */
export function tapAheadOfDucker(model: DeviceModel, plan: Plan, conn: PlanConnection): boolean {
  return channelTapAheadOfMute(model, conn.from, conn.to) || sendIsPreFader(model, plan, conn);
}

export function canConnect(model: DeviceModel, plan: Plan, from: string, to: string): ConnectResult {
  const rule = findRule(model, from, to);
  if (!rule) return { ok: false, reason: "noRule" };
  if (hasConnection(plan, from, to)) return { ok: false, reason: "duplicate" };
  // A single-input receiver rejects a second incoming wire, counting any existing
  // wire into the port regardless of its stored kind (a malformed/garbled plan
  // could carry a wrong-kind wire into the slot). Summing receivers have a
  // non-single-input rule.kind, so their fan-in stays unrestricted. A USB output
  // holding one channel of a mono pair takes the other channel's wire, and that
  // is the only second wire it takes.
  if (isSingleInput(rule.kind)) {
    const held = plan.connections.filter((c) => c.to === to);
    if (held.length > 0) {
      if (rule.kind !== "patch" || monoPairsInto(model, to).length === 0) return { ok: false, reason: "singleInput" };
      if (held.length === 1 && monoPairOf(model, to, [held[0].from, from])) return { ok: true };
      return { ok: false, reason: "monoPairOnly" };
    }
  }
  return { ok: true };
}

// One illegal wire found by validatePlan: its endpoints and why it is rejected.
export interface PlanProblem {
  from: string;
  to: string;
  reason: ConnectError;
}

// Validate a complete plan's connections against the model's routing rules,
// returning every illegal wire. Unlike canConnect (which vets one new wire
// against the live plan), this checks an already-built plan: a wire is a problem
// when no rule matches (noRule), a single-input receiver carries more than one
// incoming wire (singleInput — reported for each wire into that port so every
// offender is listed; monoPairOnly for a USB output, whose two wires are legal
// only as the two channels of one mono pair), or the same from->to pair appears
// twice (duplicate). deserialize already drops structurally malformed elements,
// so only routing-legality issues remain to report here.
export function validatePlan(model: DeviceModel, plan: Plan): PlanProblem[] {
  const incoming = new Map<string, string[]>();
  for (const c of plan.connections) incoming.set(c.to, [...(incoming.get(c.to) ?? []), c.from]);
  const problems: PlanProblem[] = [];
  const seen = new Set<string>();
  for (const c of plan.connections) {
    const kind = ruleKind(model, c.from, c.to);
    const froms = incoming.get(c.to) ?? [];
    if (kind === undefined) {
      problems.push({ from: c.from, to: c.to, reason: "noRule" });
    } else if (isSingleInput(kind) && froms.length > 1) {
      const pairs = kind === "patch" ? monoPairsInto(model, c.to) : [];
      if (pairs.length === 0) problems.push({ from: c.from, to: c.to, reason: "singleInput" });
      else if (!monoPairOf(model, c.to, froms)) problems.push({ from: c.from, to: c.to, reason: "monoPairOnly" });
    }
    const key = `${c.from} ${c.to}`;
    if (seen.has(key)) problems.push({ from: c.from, to: c.to, reason: "duplicate" });
    seen.add(key);
  }
  return problems;
}

/** The other channel of the mono pair containing `nodeId`, if any: the channel that
 *  shares its input source, and the one that joins it on a USB output as a pair. */
export function partnerChannel(model: DeviceModel, nodeId: string): string | undefined {
  for (const [a, b] of model.channelPairs) {
    if (a === nodeId) return b;
    if (b === nodeId) return a;
  }
  return undefined;
}

/** The primary (odd, first-listed) channel of the pair containing `nodeId`, or
 *  null when it is not part of a pair. Pair-level state (Signal Type, PAN/BAL)
 *  lives on the primary. */
export function pairPrimary(model: DeviceModel, nodeId: string): string | null {
  for (const [a, b] of model.channelPairs) if (a === nodeId || b === nodeId) return a;
  return null;
}

/** The mono pairs `to` takes as two wires, primary first: every channelPairs entry both
 *  of whose channels have a `patch` rule into it. Only a USB output has channel patch
 *  rules, so every other receiver takes none. */
export function monoPairsInto(model: DeviceModel, to: string): Array<[string, string]> {
  return model.channelPairs.filter(
    ([a, b]) => ruleKind(model, ref(a, "out"), to) === "patch" && ruleKind(model, ref(b, "out"), to) === "patch",
  );
}

/** The mono pair the wires from `froms` (out refs) make into `to`, primary first
 *  whichever order they are in — or null unless they are exactly the two channels of a
 *  pair `to` takes. Two wires into a USB output mean that pair and nothing else: the
 *  output is written L = the primary's slot, R = the partner's. */
export function monoPairOf(model: DeviceModel, to: string, froms: readonly string[]): [string, string] | null {
  if (froms.length !== 2) return null;
  const [x, y] = froms.map((f) => parseRef(f).nodeId);
  return monoPairsInto(model, to).find(([a, b]) => (x === a && y === b) || (x === b && y === a)) ?? null;
}

/** The partner a patch of `out` onto the USB output `into` takes along: the other channel of
 *  a STEREO-linked mono pair, when that output accepts it and `shown` says it is on the board.
 *  Null for anything else. */
export function linkedPatchPartner(
  model: DeviceModel,
  plan: Plan,
  out: string,
  into: string,
  shown: (nodeId: string) => boolean = () => true,
): string | null {
  const node = parseRef(out).nodeId;
  if (!isStereoLinkedPair(model, plan, node)) return null;
  const pair = monoPairsInto(model, into).find(([a, b]) => a === node || b === node);
  if (!pair) return null;
  const partnerNode = pair[0] === node ? pair[1] : pair[0];
  if (!shown(partnerNode)) return null;
  const partner = ref(partnerNode, "out");
  return canConnect(model, plan, partner, into).ok ? partner : null;
}

/** A patch of `out` onto `into` that the output already holds, which completes a linked pair
 *  there: the one refused wire a drop still takes, adding the partner instead. */
export function completesLinkedPatch(
  model: DeviceModel,
  plan: Plan,
  out: string,
  into: string,
  shown: (nodeId: string) => boolean = () => true,
): boolean {
  return (
    ruleKind(model, out, into) === "patch" &&
    canConnect(model, plan, out, into).reason === "duplicate" &&
    linkedPatchPartner(model, plan, out, into, shown) !== null
  );
}

/** What a drop of `out` onto `into` leaves wired, as the board takes it: a wire into an input
 *  that takes one source replaces the one there (`replaces`), a channel's source is mirrored
 *  onto its pair partner, and a STEREO-linked pair goes onto a USB output together. A drop
 *  the board refuses gives the reason, when the rules name one. */
export function dropWire(
  model: DeviceModel,
  plan: Plan,
  out: string,
  into: string,
  shown: (nodeId: string) => boolean = () => true,
): { ok: true; connections: PlanConnection[]; replaces: boolean } | { ok: false; reason?: ConnectError } {
  const kind = ruleKind(model, out, into);
  const result = canConnect(model, plan, out, into);
  const replaces = result.reason === "singleInput" || result.reason === "monoPairOnly";
  if (!result.ok && !replaces && !completesLinkedPatch(model, plan, out, into, shown))
    return { ok: false, reason: result.reason };
  let connections = replaces ? plan.connections.filter((c) => c.to !== into) : [...plan.connections];
  // canConnect rejects a route with no rule, so reaching here means kind is set.
  if (result.ok || replaces) connections.push({ from: out, to: into, kind: kind! });
  if (kind === "source") {
    const { nodeId, portId } = parseRef(into);
    const partner = partnerChannel(model, nodeId);
    if (partner) {
      const mirrorTo = ref(partner, portId);
      connections = connections.filter((c) => !(c.to === mirrorTo && isSingleInput(c.kind)));
      if (!connections.some((c) => c.from === out && c.to === mirrorTo))
        connections.push({ from: out, to: mirrorTo, kind: "source" });
    }
  }
  if (kind === "patch") {
    const partner = linkedPatchPartner(model, { ...plan, connections }, out, into, shown);
    if (partner) connections.push({ from: partner, to: into, kind: "patch" });
  }
  return { ok: true, connections, replaces };
}

/** True when `id` belongs to a MONO IN pair whose Signal Type is STEREO, whatever its
 *  PAN/BAL mode (the flag lives on the primary, so it is read there). This is the gate for
 *  everything the pair holds in common — its tuning, its mixer state and the one insert
 *  effect its two members point at — and only a Signal Type transition clears it. The
 *  PAN/BAL mode decides one thing on top of it, the pan: see isBalLinkedPair. */
export function isStereoLinkedPair(model: DeviceModel, plan: Plan, id: string): boolean {
  const primary = pairPrimary(model, id);
  return primary !== null && plan.nodeParams[primary]?.stereoLink === true;
}

/** True when `id` belongs to a STEREO-linked MONO IN pair currently in BAL mode.
 *  A linked pair mirrors its mixer parameters in either mode; what BAL decides is the
 *  PAN — one shared balance for the pair, where PAN mode leaves each channel its own. */
export function isBalLinkedPair(model: DeviceModel, plan: Plan, id: string): boolean {
  const primary = pairPrimary(model, id);
  if (!primary || !isStereoLinkedPair(model, plan, id)) return false;
  return (plan.nodeParams[primary]?.panBal ?? PAN_BAL_PAN) === PAN_BAL_BAL;
}

/** Apply what the unit does to a MONO IN pair's pan positions and insert FX when its
 *  Signal Type or PAN/BAL changes (`primary` names the pair; `patch` is the edit that landed).
 *  Measured on the unit: linking leaves the pair in BAL and an unlinked pair reads
 *  PAN, and each of the three transitions slams CH_PAN and every bus send's pan
 *  together — PAN hard-pans the odd channel left and the even one right, BAL and
 *  unlinking centre both. A channel's CH_PAN is the pan of its fixed send into
 *  STEREO, so the send loop covers it; the SD Rec assign is a `sendSwitch` and has
 *  no pan. The head amp is untouched here as it is in the mirror: linking leaves each member
 *  the input stage it had. Call it before `mirrorLinkedPair` so the mirror copies settled values.
 *
 *  Returns the contest keys it wrote, for the caller's write witness. Every one of them
 *  can be written without MOVING — unlinking a BAL pair centres pans that are already
 *  centred, and clears an insert FX on a member that never had one — so the plan's own
 *  diff cannot tell them from a key nobody touched, and a device read in flight takes
 *  them all back. What the gesture asserts is the settled state, not the delta. */
export function applyPairTransition(model: DeviceModel, plan: Plan, primary: string, patch: NodeParams): string[] {
  const written: string[] = [];
  const pair = model.channelPairs.find(([a]) => a === primary);
  if (!pair) return written;
  if (patch.stereoLink !== undefined) {
    const np = plan.nodeParams[primary];
    plan.nodeParams[primary] = { ...np, panBal: patch.stereoLink ? PAN_BAL_BAL : PAN_BAL_PAN };
    written.push(nodeParamContestKey(primary, "panBal"));
    // Measured: a Signal Type transition clears the insert-FX selector and its ON on BOTH
    // members, in either direction and whichever member was holding the effect. Follow it —
    // a selection left in the plan would make the next live converge re-select an effect the
    // unit has just dropped. The selector and its ON are SET to No Effect and off rather than
    // removed: an absent selector is one the write sends nothing for, so a unit still holding
    // an effect would keep it. The stored engine values go: they are read through the selected
    // family, so a cleared selector leaves them nothing to bind to.
    for (const ch of pair) {
      // Named whether or not there was anything to clear: the assertion is that this
      // member carries no insert FX afterwards, which is as true of one that had none.
      for (const key of INSERT_FX_PAIR_KEYS) written.push(nodeParamContestKey(ch, key));
      const cp = plan.nodeParams[ch];
      if (!cp) continue;
      cp.insertFx = INSERT_FX_NONE;
      cp.insertFxOn = false;
      delete cp.insertFxParams;
    }
  }
  const centre = plan.nodeParams[primary]?.stereoLink !== true || isBalLinkedPair(model, plan, primary);
  pair.forEach((ch, idx) => {
    const pan = centre ? 0 : idx === 0 ? -STEREO_PAN_DEFAULT : STEREO_PAN_DEFAULT;
    for (const c of plan.connections)
      if (c.from === ref(ch, "out") && c.kind === "send") {
        c.params = { ...c.params, pan };
        written.push(connParamContestKey(c.from, c.to, "pan"));
      }
  });
  return written;
}

/** The node params a linked pair does NOT share: the pair-level flags, which live on the
 *  primary alone, and the polarity each member keeps its own of. The input stage is no
 *  channel's: it is on the jack each member reads, and two jacks are two holders. */
export const PAIR_OWN_NODE_KEYS = ["stereoLink", "panBal", "phase"] as const;

/** Whether an edit to `path` on one member of a linked pair reaches the other — the one
 *  place that question is answered, so `mirrorLinkedPair`'s copy, the MIDI catalogue's
 *  mirror identity and each funnel's write witness cannot disagree about it. Takes a
 *  contest path as well as a bare key: a nested group (`gate.threshold`) is shared when
 *  its group is. */
export function pairSharesNodeKey(path: string): boolean {
  const head = path.split(".")[0];
  return !(PAIR_OWN_NODE_KEYS as readonly string[]).includes(head);
}

/** Mirror `id`'s mixer state onto its linked partner, so an edit to either channel moves
 *  both. Copies the node params except `PAIR_OWN_NODE_KEYS`, and each send's mix params —
 *  level / PRE-POST / ON always, and the pan in BAL only.
 *
 *  **The gate is Signal Type, not PAN/BAL**: a linked pair holds one set of values in either
 *  mode. What the mode decides is the pan, each member's own outside BAL — which is why the
 *  send copy drops that one key there.
 *
 *  Returns false — a no-op — unless the pair is STEREO-linked. */
export function mirrorLinkedPair(model: DeviceModel, plan: Plan, id: string): boolean {
  if (!isStereoLinkedPair(model, plan, id)) return false;
  const partner = partnerChannel(model, id);
  if (!partner) return false;
  const sharedPan = isBalLinkedPair(model, plan, id);
  // Replace the partner's node params with the source's, but keep the keys the pair does not
  // share: the pair-level fields (only the primary carries stereoLink / panBal) and the
  // polarity (each member keeps its own). The copy is deep: a shallow spread would alias the
  // nested groups (gate / comp / eqBands / ssmcs / osc / eqOneKnob) between the two channels,
  // so an in-place edit to one would bleed into the other — and the alias would outlive the
  // link, since it persists until a replace-style edit or a JSON round-trip breaks it.
  // The insert FX travels with them, as it does on the unit: while the pair is linked a
  // selector write from either member mirrors to the other and both point at one engine
  // instance, so a linked pair holds one insert effect between them. mirrorLinkedInsertFx
  // carries the same three keys from the transition seat, and the two agree.
  const src = structuredClone(plan.nodeParams[id] ?? {});
  const own = plan.nodeParams[partner] ?? {};
  // An absent key stays absent on the partner: one held as undefined would outlive the copy
  // through the history differ and the JSON round-trip.
  const kept = Object.fromEntries(
    PAIR_OWN_NODE_KEYS.filter((k) => own[k] !== undefined).map((k) => [k, own[k]]),
  ) as Partial<NodeParams>;
  for (const key of PAIR_OWN_NODE_KEYS) delete src[key];
  plan.nodeParams[partner] = { ...src, ...kept };
  // Copy each send's mix params to the partner's send into the same destination. ConnParams
  // are flat scalars, so the spread is a full copy. The PAN is dropped outside BAL: there it
  // is the pair's one shared balance and belongs to both, while in PAN mode each member
  // holds its own and the partner keeps what it has.
  for (const c of plan.connections) {
    if (c.kind !== "send" || c.from !== ref(id, "out")) continue;
    const pc = plan.connections.find((p) => p.kind === "send" && p.from === ref(partner, "out") && p.to === c.to);
    if (!pc) continue;
    const from = c.params ?? {};
    if (sharedPan) {
      pc.params = { ...pc.params, ...from };
    } else {
      const { pan: _pan, ...rest } = from;
      pc.params = { ...pc.params, ...rest };
    }
  }
  return true;
}

/** Mirror `id`'s insert FX onto its linked partner, so the pair's one effect reads the
 *  same on both members. Called beside mirrorLinkedPair from every edit funnel: the two
 *  share a gate and write the same values, and this pass is what names the pair's three
 *  insert-FX keys as the edit's own writes whatever the edit was — without it a device
 *  read in flight takes back an effect the plan holds on both members, and the next flush
 *  writes NONE over what the unit holds on the other one. The engine values are
 *  deep-copied for the aliasing reason mirrorLinkedPair documents.
 *  Returns false — a no-op — unless the pair is STEREO-linked. */
/** The pair state this mirror carries, and the whole of it — the caller that has to name
 *  what the mirror asserted reads the same list rather than keeping a second copy. */
export const INSERT_FX_PAIR_KEYS = ["insertFx", "insertFxOn", "insertFxParams"] as const;

export function mirrorLinkedInsertFx(model: DeviceModel, plan: Plan, id: string): boolean {
  if (!isStereoLinkedPair(model, plan, id)) return false;
  const partner = partnerChannel(model, id);
  if (!partner) return false;
  const { insertFx, insertFxOn, insertFxParams } = plan.nodeParams[id] ?? {};
  const dst = (plan.nodeParams[partner] ??= {});
  // An absent key stays absent on the partner: a key held as undefined would outlive
  // the copy through the history differ and the JSON round-trip.
  for (const key of INSERT_FX_PAIR_KEYS) delete dst[key];
  if (insertFx !== undefined) dst.insertFx = insertFx;
  if (insertFxOn !== undefined) dst.insertFxOn = insertFxOn;
  if (insertFxParams !== undefined) dst.insertFxParams = structuredClone(insertFxParams);
  return true;
}

/** Whether a node reads as inactive (silenced) and should be dimmed alike in both
 *  views: a muted node (CH_ON / a bus / FX / MONITOR master ON — all on `params.on`),
 *  a bypassed ducker (`duckerOn`) or the oscillator when not generating (`osc.on`).
 *  Each off-state lives on a different param, so each kind needs its own check. */
export function isNodeInactive(plan: Plan, node: { id: string; kind: NodeKind }): boolean {
  const np = plan.nodeParams?.[node.id];
  if (node.kind === "ducker") return np?.duckerOn !== true;
  if (node.id === "bus.osc") return np?.osc?.on !== true;
  return np?.on === false;
}

/** Input-port refs that the given output port may currently connect to. */
export function legalTargets(model: DeviceModel, plan: Plan, from: string): Set<string> {
  const targets = new Set<string>();
  for (const rule of model.rules) {
    if (rule.from !== from) continue;
    if (canConnect(model, plan, from, rule.to).ok) targets.add(rule.to);
  }
  return targets;
}

/** Output-port refs that may currently connect into the given input port. */
export function legalSources(model: DeviceModel, plan: Plan, to: string): Set<string> {
  const sources = new Set<string>();
  for (const rule of model.rules) {
    if (rule.to !== to) continue;
    if (canConnect(model, plan, rule.from, to).ok) sources.add(rule.from);
  }
  return sources;
}

/** Input-port refs the given output has a routing rule to, occupied ones
 *  included. A superset of legalTargets: it ignores the current plan, so a
 *  single-input target that is already full still appears. Used to show where a
 *  port *could* route even when the destination is taken. */
export function possibleTargets(model: DeviceModel, from: string): Set<string> {
  const targets = new Set<string>();
  for (const rule of model.rules) if (rule.from === from) targets.add(rule.to);
  return targets;
}

/** Output-port refs that have a routing rule into the given input, occupied ones
 *  included. The input-side counterpart of possibleTargets. */
export function possibleSources(model: DeviceModel, to: string): Set<string> {
  const sources = new Set<string>();
  for (const rule of model.rules) if (rule.to === to) sources.add(rule.from);
  return sources;
}

/** Node ids in the upstream signal closure feeding `nodeId` (inclusive): every
 *  node that reaches it by walking connections backwards. `live` filters which
 *  connections to follow — pass a predicate that rejects silent (off / -∞) sends,
 *  otherwise the always-wired send mesh traces every node back to all inputs and
 *  the closure becomes the whole board. `feeders` names nodes that shape a node's signal
 *  without a wire into it (a ducker over its channel), given the wire the walk reached
 *  the node through — null for the node itself and for a node reached as a feeder — since
 *  what shapes a node's signal can depend on where it is taken. A node is walked again
 *  when reached with different feeders. Cycle-safe via the visited states. */
export function upstreamNodes(
  plan: Plan,
  nodeId: string,
  live: (conn: PlanConnection) => boolean,
  feeders: (nodeId: string, via: PlanConnection | null) => readonly string[] = () => [],
): Set<string> {
  const closure = new Set<string>();
  const seen = new Set<string>();
  const stack: [string, PlanConnection | null][] = [[nodeId, null]];
  while (stack.length) {
    const [cur, via] = stack.pop()!;
    const extra = feeders(cur, via);
    const state = [cur, ...extra].join("\n");
    if (seen.has(state)) continue;
    seen.add(state);
    closure.add(cur);
    for (const id of extra) stack.push([id, null]);
    for (const conn of plan.connections) {
      if (parseRef(conn.to).nodeId === cur && live(conn)) stack.push([parseRef(conn.from).nodeId, conn]);
    }
  }
  return closure;
}
